/*!
// <copyright file="Messaging.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/* jshint -W040 */
"use strict";

/* jshint +W040 */

import { util } from "./util";

export namespace messaging {

    /// <field name="MessageTypes" type="Object">Message types.</field>
    export let MessageTypes = {

        /// <field type="String">The message type for a 'ResourceReady' request.</field>
        resourceReadyRequest: "ResourceReadyRequest",

        /// <field type="String">The message type for a 'CheckAuthentication' request.</field>
        checkAuthenticationRequest: "CheckAuthenticationRequest",

        /// <field type="String">The message type for a 'CheckAuthentication' response.</field>
        checkAuthenticationResponse: "CheckAuthenticationResponse",

        /// <field type="String">The message type for a 'CrossDomainCheckAuthentication' request.</field>
        crossDomainCheckAuthenticationRequest: "CrossDomainCheckAuthenticationRequest",

        /// <field type="String">The message type for a 'CrossDomainCheckAuthentication' response.</field>
        crossDomainCheckAuthenticationResponse: "CrossDomainCheckAuthenticationResponse",

        /// <field type="String">The message type for a 'GetConfig' request.</field>
        getConfigRequest: "GetConfigRequest",

        /// <field type="String">The message type for a 'GetConfig' response.</field>
        getConfigResponse: "GetConfigResponse",

        /**
         * The message type for a 'GetEnhancedConfig' request.
         */
        getEnhancedConfigRequest: "GetEnhancedConfigRequest",

        /**
         * The message type for a 'GetEnhancedConfig' response.
         */
        getEnhancedConfigResponse: "GetEnhancedConfigResponse",

        /// <field type="String">The message type for a 'GetData' request.</field>
        getDataRequest: "GetDataRequest",

        /// <field type="String">The message type for a 'GetData' response.</field>
        getDataResponse: "GetDataResponse",

        /// <field type="String">The message type for a 'GetCount' request.</field>
        getCountRequest: "GetCountRequest",

        /// <field type="String">The message type for a 'GetCount' response.</field>
        getCountResponse: "GetCountResponse",

        /// <field type="String">The message type for a 'GetMetadata' request.</field>
        getMetadataRequest: "GetMetadataRequest",

        /// <field type="String">The message type for a 'GetMetadata' response.</field>
        getMetadataResponse: "GetMetadataResponse",

        /// <field type="String">The message type for a 'GetLabels' request.</field>
        getLabelsRequest: "GetLabelsRequest",

        /// <field type="String">The message type for a 'GetLabels' response.</field>
        getLabelsResponse: "GetLabelsResponse",

        /// <field type="String">The message type for a 'GetCustomLookup' request.</field>
        getCustomLookupRequest: "GetCustomLookupRequest",

        /// <field type="String">The message type for a 'GetCustomLookup' response.</field>
        getCustomLookupResponse: "GetCustomLookupResponse",

        /// <field type="String">The message type for a 'GetDisplayValue' request.</field>
        getDisplayValueRequest: "GetDisplayValueRequest",

        /// <field type="String">The message type for a 'GetDisplayValue' response.</field>
        getDisplayValueResponse: "GetDisplayValueResponse",

        /// <field type="String">The message type for a 'GetEntities' request.</field>
        getEntitiesRequest: "GetEntitiesRequest",

        /// <field type="String">The message type for a 'GetEntities' response.</field>
        getEntitiesResponse: "GetEntitiesResponse",

        /// <field type="String">The message type for a 'PublishData' request.</field>
        publishDataRequest: "PublishDataRequest",

        /// <field type="String">The message type for a 'PublishData' response.</field>
        publishDataResponse: "PublishDataResponse",

        /// <field type="String">The message type for a 'GetUpdatedData' request.</field>
        getUpdatedDataRequest: "GetUpdatedDataRequest",

        /// <field type="String">The message type for a 'GetUpdatedData' response.</field>
        getUpdatedDataResponse: "GetUpdatedDataResponse",

        /// <field type="String">The message type for a 'ConfigureResource' request.</field>
        configureResourceRequest: "ConfigureResource",

        /// <field type="String">The message type for an 'ODataCallAttempted' response.</field>
        odataCallAttempted: "ODataCallAttempted",

        /// <field type="String">The message type for an 'ODataCallThrottled' response.</field>
        odataCallThrottled: "ODataCallThrottled",

        /// <field type="String">The message type for an 'ODataCallRefreshHeaders' response.</field>
        odataCallRefreshHeaders: "ODataCallRefreshHeaders",

        /// <field type="String">The message type for an 'unknown message type' response.</field>
        unknownMessageTypeResponse: "UnknownMessageTypeResponse",
    }

    /// <field name="ResponseStatus" type="Object">The known statuses for a response.</field>
    export let ResponseStatus = {

        /// <field type="String">The status for a successful response.</field>
        success: "Success",

        /// <field type="String">The status for a failed response.</field>
        failure: "Failure"
    }

    export class BaseRequest {
        /// <summary>
        /// Represents the base request, which has only the basics we need for a given request.
        /// </summary>
        /// <field name="Type" type="String">The type of request.</field>
        /// <field name="ResourceID" type="String">The ID of the resource for which the message was intended.</field>
        /// <field name="MessageID" type="String">The ID of the message.</field>

        public ResourceID: string;
        public MessageID: string;

        constructor(public Type: string = "") { }
    }

    /// <field name="Requests" type="Object">The request messages the apps will handle.</field>
    export namespace Requests {

        export class ResourceReadyRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'ResourceReady' request.
            /// </summary>
            /// <field name="ResourceID" type="String">The ID of the resource that is ready.</field>

            constructor(public ResourceID: string) {
                super(messaging.MessageTypes.resourceReadyRequest);
            }
        }

        export class CheckAuthenticationRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'CheckAuthentication' request.
            /// </summary>
            /// <field name="ForceRefresh" type="Boolean">Indicates if a refresh of the authentication page should be forced.</field>
            public ForceRefresh = false;

            constructor() {
                super(messaging.MessageTypes.checkAuthenticationRequest);
            }
        }

        export class CrossDomainCheckAuthenticationRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'CrossDomainCheckAuthentication' request.
            /// </summary>

            constructor() {
                super(messaging.MessageTypes.crossDomainCheckAuthenticationRequest);
            }
        }

        export class GetConfigRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetConfig' request.
            /// </summary>

            constructor() {
                super(messaging.MessageTypes.getConfigRequest);
            }
        }

        /**
         * Represents the 'GetEnhancedConfig' request.
         */
        export class GetEnhancedConfigRequest extends BaseRequest {

            /**
             * Complete URL to retrieve the enhanced server configuration from.
             */
            public EnhancedConfigUrl: string;

            /**
             * Group of headers to add to the request for the enhanced server configuration.
             */
            public Headers: util.PropertyBag<string>;

            constructor() {
                super(messaging.MessageTypes.getEnhancedConfigRequest);
            }
        }

        export class GetDataRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetData' request.
            /// </summary>
            /// <field name="EntityName" type="String">The name of the entity to retrieve data from.</field>
            /// <field name="EntitySetName" type="String">The name of the entity set to retrieve data from.</field>
            /// <field name="FilterExpression" type="String">The filter expression to apply when retrieving data.</field>
            /// <field name="FieldSelectList" type="Array" elementType="String">The names of the fields to select.</field>
            /// <field name="ExpandRelations" type="Array" elementType="String">The names of the relations to expand, eagerly loading referenced data.</field>
            /// <field name="MaxResults" type="Number">The max number of results to return.</field>
            /// <field name="RelationName" type="String">The name of the relation used to select an entity.</field>
            /// <field name="Key" type="Object">The property bag representing a key used when selecting an entity using a relation.</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>

            public EntityName = "";
            public EntitySetName = "";
            public FilterExpression: string;
            public FieldSelectList: string[];
            public ExpandRelations: string[];
            public MaxResults: number;
            public RelationName: string;
            public Key: any;
            public Headers: any;

            constructor() {
                super(messaging.MessageTypes.getDataRequest);
                this.Headers = {};
            }
        }

        export class GetCountRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetCount' request.
            /// </summary>
            /// <field name="EntityName" type="String">The name of the entity to count.</field>
            /// <field name="EntitySetName" type="String">The name of the entity set to count.</field>
            /// <field name="FilterExpression" type="String">The filter expression to apply when counting the data.</field>
            /// <field name="RelationName" type="String">The name of the relation used to select an entity.</field>
            /// <field name="Key" type="Object">The property bag representing a key used when selecting an entity using a relation.</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>

            public EntityName = "";
            public EntitySetName = "";
            public FilterExpression: string;
            public RelationName: string;
            public Key: any;
            public Headers: any;

            constructor() {
                super(messaging.MessageTypes.getCountRequest);
                this.Headers = {};
            }
        }

        export class GetEntitiesRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetEntities' request, which gets a list of all data entities.
            /// </summary>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>

            public Headers: any;
            public IncludeLabels: boolean = false;

            constructor() {
                super(messaging.MessageTypes.getEntitiesRequest);
                this.Headers = {};
            }
        }

        export class GetMetadataRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetMetadata' request.
            /// </summary>
            /// <field name="Entities" type="Object">The property bag of entities for which metadata will be loaded.</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>

            public Entities: any;
            public Headers: any;

            constructor() {
                super(messaging.MessageTypes.getMetadataRequest);
                this.Entities = {};
                this.Headers = {};
            }
        }

        export class GetLabelsRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetLabels' request.
            /// </summary>
            /// <field name="LabelIDs" type="Array" elementType="String">The label IDs that should be resolved.</field>
            /// <field name="Language" type="String">The language to resolve the label IDs against.</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>

            public LabelIDs: string[];
            public Language: string;
            public Headers: any;

            constructor() {
                super(messaging.MessageTypes.getLabelsRequest);
                this.LabelIDs = [];
                this.Headers = {};
            }
        }

        export class GetCustomLookupRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetCustomLookup' request.
            /// </summary>
            /// <field name="CustomLookupMethod" type="String">The name of the custom lookup method to call.</field>
            /// <field name="EntityCollectionName" type="String">The entity collection name for the field the custom lookup is being performed for.</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>
            /// <field name="Fields" type="Array" elementType="messaging.CustomLookupFieldDetails">The field and corresponding value pairs that represent the currently selected row's context.</field>

            public CustomLookupMethod = "";
            public EntityCollectionName = "";
            public Headers: any;
            public Fields: string[];

            constructor() {
                super(messaging.MessageTypes.getCustomLookupRequest);
                this.Headers = {};
                this.Fields = [];
            }
        }

        export class GetDisplayValueRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetDisplayValue' request.
            /// </summary>
            /// <field name="EntityName" type="String">The name of the entity containing the display value to resolve.</field>
            /// <field name="FieldName" type="String">The name of the private field on the entity that matches the display value to resolve.</field>
            /// <field name="AttributeNames" type="Array" elementType="String">An array of dimension attribute field names.</field>
            /// <field name="AttributeValues" type="Array" elementType="Object">An array of dimension attribute field values, matching the elements in the AttributeNames array.</field>
            /// <field name="TypeFieldValue" type="String">The value of the type field if the dimension is dynamic.</field>
            /// <field name="DimensionEntityCollectionName" type="String">The collection name of the dimension entity (either the combination or set entity).</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>

            public EntityName = "";
            public FieldName = "";
            public AttributeNames: string[];
            public AttributeValues: any[];
            public TypeFieldValue: string;
            public DimensionEntityCollectionName: string = "";
            public Headers: any;

            constructor() {
                super(messaging.MessageTypes.getDisplayValueRequest);
                this.AttributeNames = [];
                this.AttributeValues = [];
                this.Headers = {};
            }
        }

        /** Represents a publish request that needs to be performed after another request. */
        export interface SubsequentPublishRequest {

            /** The request that should be performed after another request. */
            Request: PublishDataRequest;
        }

        /** Represents a request related to another request, that should be performed after the other request. */
        export interface RelatedPublishRequest extends SubsequentPublishRequest {

            /** The fields that relate the requests' entities, keyed by field name with related field names as the values. */
            RelatedFields: util.PropertyBag<string>;
        }

        export class PublishDataRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'PublishData' request.
            /// </summary>
            /// <field name="EntitySetName" type="String">The name of the entity set for which data is to be published.</field>
            /// <field name="DataChanges" type="messaging.DataChanges">A messaging.DataChanges object representing the data changes to be published.</field>
            /// <field name="DataChangeSuccessesPlaceholder" type="messaging.DataChanges">A messaging.DataChanges object acting as a placeholder for the successful results of a PublishDataRequest.</field>
            /// <field name="DataChangeFailuresPlaceholder" type="messaging.DataChanges">A messaging.DataChanges object acting as a placeholder for the failed results of a PublishDataRequest.</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>
            /// <field name="PublishSequentially" type="Boolean">Indicates whether data should be published sequentially.</field>
            /// <field name="SubsequentRequests" type="Array" elementType="messaging.Requests.SubsequentPublishRequest">The requests that should be handled once the request has completed successfully.</field>
            /// <field name="PublishBatchLimit" type:"number">The maximum number of data entries to publish in one batch.</field>

            public EntityName = "";
            public EntitySetName = "";
            public DataChanges: messaging.DataChanges;
            public DataChangeSuccessesPlaceholder: messaging.DataChanges;
            public DataChangeFailuresPlaceholder: messaging.DataChanges;
            public Headers: any;
            public PublishSequentially: boolean;
            public SubsequentRequests: SubsequentPublishRequest[];
            public PublishBatchLimit: number;

            constructor() {
                super(messaging.MessageTypes.publishDataRequest);
                this.Headers = {};
                this.PublishSequentially = false;
                this.SubsequentRequests = [];
                this.PublishBatchLimit = 100;
            }
        }

        export class GetUpdatedDataRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'GetUpdatedData' request.
            /// </summary>
            /// <field name="EntityName" type="String">The name of the entity.</field>
            /// <field name="EntitySetName" type="String">The name of the entity set for which updated data is to be retrieved.</field>
            /// <field name="ExpandRelations" type="Array" elementType="String">The names of the relations to expand, eagerly loading referenced data.</field>
            /// <field name="DataChangeSuccesses" type="messaging.DataChanges">A messaging.DataChanges object representing the successful results of a GetUpdatedDataRequest.</field>
            /// <field name="DataChangeFailures" type="messaging.DataChanges">A messaging.DataChanges object representing the failed results of a GetUpdatedDataRequest.</field>
            /// <field name="UpdateCreates" type="Boolean">Indicates whether successfully created rows should be updated as well.</field>
            /// <field name="Headers" type="Object">The additional headers to apply to the OData request.</field>

            public EntityName = "";
            public EntitySetName = "";
            public ExpandRelations: string[];
            public DataChangeSuccesses: messaging.DataChanges;
            public DataChangeFailures: messaging.DataChanges;
            public UpdateCreates = false;
            public Headers: any;

            constructor() {
                super(messaging.MessageTypes.getUpdatedDataRequest);
                this.ExpandRelations = [];
                this.Headers = {};
            }
        }

        export class ConfigureResourceRequest extends BaseRequest {
            /// <summary>
            /// Represents the 'ConfigureResource' request.
            /// </summary>
            /// <param name="host" type="String">The host from to which the app is connected.</param>
            /// <param name="serverConfig" type="Object">The host's server configuration.</param>
            /// <field name="Host" type="String">The host from to which the app is connected.</field>
            /// <field name="ServerConfig" type="Object">The host's server configuration.</field>
            /// <field name="AppFeatures" type="Object">The app's capabilities / features.</field>

            constructor(public Host: string, public ServerConfig: any, public AppFeatures?: util.AddinFeatures) {
                super(messaging.MessageTypes.configureResourceRequest);
            }
        }
    }

    export class BaseResponse<TRequest extends BaseRequest> {
        /// <summary>
        /// Represents the base response, which has only the basics we need for a given response.
        /// </summary>
        /// <field name="Type" type="String">The type of request.</field>
        /// <field name="Status" type="String">The status of the request.</field>
        /// <field name="FailureReason" type="String">The reason the request failed, or empty string if request was successful.</field>
        /// <field name="Request" type="messaging.BaseRequest">The originating request that resulted in the response.</field>

        public Type = "";
        public Status = "";
        public FailureReason = "";
        public Request: TRequest;

        constructor(messageType: string = "") {
            this.Type = messageType;
        }
    }

    /// <field name="Responses" type="Object">The response messages the apps will handle.</field>
    export namespace Responses {

        export class CheckAuthenticationResponse extends BaseResponse<Requests.CheckAuthenticationRequest> {
            /// <summary>
            /// Represents the 'CheckAuthentication' response.
            /// </summary>
            /// <field name="Ready" type="Boolean">Indicates if the authentication page is ready.</field>
            /// <field name="Authenticated" type="Boolean">Indicates if the user is authenticated.</field>
            /// <field name="OriginSet" type="Object">A property bag of valid origins, with origin as the key and value.</field>

            public Ready = false;
            public Authenticated = false;
            public OriginSet: any;

            constructor() {
                super(messaging.MessageTypes.checkAuthenticationResponse);
                this.OriginSet = {};
            }
        }

        export class CrossDomainCheckAuthenticationResponse extends BaseResponse<Requests.CrossDomainCheckAuthenticationRequest> {
            /// <summary>
            /// Represents the 'CrossDomainCheckAuthentication' response.
            /// </summary>

            constructor() {
                super(messaging.MessageTypes.crossDomainCheckAuthenticationResponse);
            }
        }

        export class GetConfigResponse extends BaseResponse<Requests.GetConfigRequest> {
            /// <summary>
            /// Represents the 'GetConfig' response.
            /// </summary>
            /// <field name="Config" type="Object">The configuration object retrieved from the server.</field>

            public Config: any;

            constructor() {
                super(messaging.MessageTypes.getConfigResponse);
                this.Config = {};
            }
        }

        /**
         * Represents the 'GetEnhancedConfig' response.
         */
        export class GetEnhancedConfigResponse extends BaseResponse<Requests.GetEnhancedConfigRequest> {

            /** The configuration object retrieved from the server. */
            public Config: any;

            constructor() {
                super(messaging.MessageTypes.getEnhancedConfigResponse);
                this.Config = {};
            }
        }

        /** Represents the base OData response. */
        export abstract class ODataResponse<TRequest extends BaseRequest> extends BaseResponse<TRequest> {

            /**
             * The activity IDs used by the OData request.
             * @deprecated
             */
            public ActivityIDs: string[];

            /** The activity details of the OData requests. */
            public Activities: DataActivity[];

            /** The HTTP status code associated with the failure. */
            public FailureStatusCode: number;

            /** Indicates if this is a partial response and that more responses are expected. */
            public Partial: boolean;

            /**
             * @param messageType The type of OData response.
             */
            constructor(messageType: string) {
                super(messageType);
                this.ActivityIDs = [];
                this.Activities = [];
                this.FailureStatusCode = 0;
                this.Partial = false;
            }
        }

        /** Represents an OData call attempted response. */
        export class ODataCallAttemptedResponse<TRequest extends BaseRequest> extends ODataResponse<TRequest> {

            /**
             * @param request The request for which an OData call attempt was made.
             * @param activity The corresponding OData activity.
             */
            constructor(request: TRequest, activity: string | DataActivity) {
                super(messaging.MessageTypes.odataCallAttempted);

                this.Partial = true;

                this.Request = new BaseRequest(request.Type) as TRequest;
                this.Request.ResourceID = request.ResourceID;
                this.Request.MessageID = request.MessageID;

                if (typeof activity === "string") {
                    this.ActivityIDs.push(activity);
                } else if (activity) {
                    this.Activities.push(activity as DataActivity);
                }
            }
        }

        /** Represents an OData call attempted response. */
        export class ODataCallThrottledResponse<TRequest extends BaseRequest> extends ODataResponse<TRequest> {

            /** The amount of time the add-in will wait before sending the next request. */
            public WaitTime: string;

            /** The retry attempt number. */
            public RetryAttempt: number;

            /** Indicates whether the request will be retried again. */
            public Retrying: boolean;

            /** The corresponding original OData activity. */
            public OriginalActivityId: string;

            /**
             * @param request The request for which an OData call attempt was made.
             * @param activity The corresponding OData activity.
             */
            constructor(
                request: TRequest,
                activity: string | DataActivity,
                waitTime: string,
                retryAttempt: number,
                retrying: boolean
            ) {
                super(messaging.MessageTypes.odataCallThrottled);

                this.Partial = true;

                this.Request = new BaseRequest(request.Type) as TRequest;
                this.Request.ResourceID = request.ResourceID;
                this.Request.MessageID = request.MessageID;

                if (typeof activity === "string") {
                    this.ActivityIDs.push(activity);
                } else if (activity) {
                    this.Activities.push(activity as DataActivity);
                }

                this.WaitTime = waitTime;
                this.RetryAttempt = retryAttempt;
                this.Retrying = retrying;
            }
        }

        /** Represents a message that can be used to refresh OData request message headers. */
        export class ODataCallRefreshHeadersMessage<TRequest extends BaseRequest> extends ODataResponse<TRequest> {

            /**
             * @param request The request for which an OData call attempt was made.
             * @param activity The corresponding OData activity.
             */
            constructor(request: TRequest) {
                super(messaging.MessageTypes.odataCallRefreshHeaders);
                this.Partial = true;
                this.Request = request;
            }
        }

        export class GetDataResponse extends ODataResponse<Requests.GetDataRequest> {
            /// <summary>
            /// Represents the 'GetData' response.
            /// </summary>
            /// <field name="EntityName" type="String">The name of the entity the data was retrieved from.</field>
            /// <field name="Data" type="Array" elementType="Object">The array of objects from the OData call.</field>

            public EntityName = "";
            public Data: any[];

            constructor() {
                super(messaging.MessageTypes.getDataResponse);
                this.Data = [];
            }
        }

        export class GetCountResponse extends ODataResponse<Requests.GetCountRequest> {
            /// <summary>
            /// Represents the 'GetCount' response.
            /// </summary>
            /// <field name="EntityName" type="String">The name of the entity the data was retrieved from.</field>
            /// <field name="Count" type="Number">The number of records matching the request.</field>

            public EntityName = "";
            public Count = 0;

            constructor() {
                super(messaging.MessageTypes.getCountResponse);
            }
        }

        export class GetEntitiesResponse extends ODataResponse<Requests.GetEntitiesRequest> {
            /// <summary>
            /// Represents the 'GetEntities' response, which returns the set of all data entities.
            /// </summary>
            /// <field name="Entities" type="Object">The map of entity names to EntityMetadata from the OData response.</field>

            public Entities: any;

            constructor() {
                super(messaging.MessageTypes.getEntitiesResponse);
                this.Entities = {};
            }
        }

        export class GetMetadataResponse extends ODataResponse<Requests.GetMetadataRequest> {
            /// <summary>
            /// Represents the 'GetMetadata' response.
            /// </summary>
            /// <field name="Entities" type="Object">The property bag of entity metadata.</field>
            /// <field name="Enums" type="Object">The property bag of enum metadata.</field>

            public Entities: any;
            public Enums: any;

            constructor() {
                super(messaging.MessageTypes.getMetadataResponse);
                this.Entities = {};
                this.Enums = {};
            }
        }

        export class GetLabelsResponse extends ODataResponse<Requests.GetLabelsRequest> {
            /// <summary>
            /// Represents the 'GetLabels' response.
            /// </summary>
            /// <field name="LabelResults" type="Array" elementType="messaging.LabelResult">The results of the label resolution.</field>

            public LabelResults: LabelResult[];

            constructor() {
                super(messaging.MessageTypes.getLabelsResponse);
                this.LabelResults = [];
            }
        }

        export class GetCustomLookupResponse extends ODataResponse<Requests.GetCustomLookupRequest> {
            /// <summary>
            /// Represents the 'GetCustomLookup' response.
            /// </summary>
            /// <field name="CustomLookupValues" type="String">The custom lookup values in the form of a serialized JSON string that is returned from the custom lookup action.</field>

            public FieldName = "";
            public CustomLookupValues = "";

            constructor() {
                super(messaging.MessageTypes.getCustomLookupResponse);
            }
        }

        export class GetDisplayValueResponse extends ODataResponse<Requests.GetDisplayValueRequest> {
            /// <summary>
            /// Represents the 'GetDisplayValue' response.
            /// </summary>
            /// <field name="DisplayValue" type="String">The resolved display value.</field>

            public DisplayValue = "";

            constructor() {
                super(messaging.MessageTypes.getDisplayValueResponse);
            }
        }

        export class PublishDataProgressResponse extends ODataResponse<Requests.PublishDataRequest> {
            /// <summary>
            /// Represents the 'PublishData' progress response.
            /// </summary>
            /// <field name="PublishedRowCount" type="Number">The number of rows that have been published by the current batch.</field>

            public PublishedRowCount = 0;

            constructor() {
                super(messaging.MessageTypes.getDisplayValueResponse);
                this.Partial = true;
            }
        }

        export class PublishDataResponse extends ODataResponse<Requests.PublishDataRequest> {
            /// <summary>
            /// Represents the 'PublishData' response.
            /// </summary>
            /// <field name="DataChangeSuccesses" type="messaging.DataChanges">A messaging.DataChanges object representing the successful results of a PublishDataRequest.</field>
            /// <field name="DataChangeFailures" type="messaging.DataChanges">A messaging.DataChanges object representing the failed results of a PublishDataRequest.</field>
            /// <field name="SuccessCount" type="Number">The number of rows successfully published.</field>
            /// <field name="FailureCount" type="Number">The number of rows that failed to publish.</field>
            /// <field name="NotAttemptedCount" type="Number">The number of rows that were not attempted to be published.</field>
            /// <field name="ErrorMessages" type="Array" elementType="String">An array of error messages encountered during the publish operation.</field>
            /// <field name="BatchTimeoutCount" type="Number">The number of data publish batches that timed out.</field>

            public DataChangeSuccesses: messaging.DataChanges;
            public DataChangeFailures: messaging.DataChanges;
            public SuccessCount = -1;
            public FailureCount = -1;
            public NotAttemptedCount = -1;
            public ErrorMessages: string[];
            public SubsequentResponses: PublishDataResponse[];
            public BatchTimeoutCount = -1;

            constructor() {
                super(messaging.MessageTypes.publishDataResponse);
                this.ErrorMessages = [];
                this.SubsequentResponses = [];
            }
        }

        export class GetUpdatedDataProgressResponse extends ODataResponse<Requests.GetUpdatedDataRequest> {
            /// <summary>
            /// Represents the 'GetUpdatedData' progress response.
            /// </summary>
            /// <field name="UpdatedRowCount" type="Number">The number of rows that have been updated with the current batch.</field>

            public UpdatedRowCount = 0;

            constructor() {
                super(messaging.MessageTypes.getUpdatedDataResponse);
                this.Partial = true;
            }
        }

        export class GetUpdatedDataResponse extends ODataResponse<Requests.GetUpdatedDataRequest> {
            /// <summary>
            /// Represents the 'GetUpdatedData' response.
            /// </summary>
            /// <field name="DataChangeSuccesses" type="messaging.DataChanges">
            /// A messaging.DataChanges object representing the successful results of a GetUpdatedDataRequest,
            /// and for which the updated rows have been updated with the most recent data from the service.
            /// </field>
            /// <field name="DataChangeFailures" type="messaging.DataChanges">A messaging.DataChanges object representing the failed results of a GetUpdatedDataRequest.</field>
            /// <field name="ErrorMessages" type="Array" elementType="String">An array of error messages encountered during the update operation.</field>

            public DataChangeSuccesses: messaging.DataChanges;
            public DataChangeFailures: messaging.DataChanges;
            public ErrorMessages: string[];

            constructor() {
                super(messaging.MessageTypes.getUpdatedDataResponse);
                this.ErrorMessages = [];
            }
        }

        export class UnknownMessageTypeResponse extends BaseResponse<BaseRequest> {
            /// <summary>
            /// Represents the 'UnknownMessageType' request.
            /// </summary>
            /// <param name="messageType" type="String">The type of message received, which wasn't able to be handled.</param>

            public FailureReason: string;

            constructor(messageType: string) {
                super(messaging.MessageTypes.unknownMessageTypeResponse);
                this.Status = messaging.ResponseStatus.failure;
            }
        }
    }

    /** The regular expression used to determine if a label is actually a label ID. */
    export const LabelIdValidationRegex = /^@.+/;

    /** The regular expression used to determine if a label is actually a delimited label ID. */
    export const DelimitedLabelIdValidationRegex = /^@[^\.]+\.[^.]+/;

    /** The expression used to determine if a label is a formatted label. */
    export const FormattedLabelExpression = /^{"?FormatLabelId"?:.+ParameterLabelIds"?:.+}$/;

    export class LabelResult {
        /// <summary>
        /// Represents the result of a label being resolved.
        /// </summary>
        /// <field name="ID" type="String">The ID of the label that was resolved.</field>
        /// <field name="Value" type="String">The resolved label value.</field>
        /// <field name="FailureReason" type="String">The reason the label resolution failed.</field>

        public ID: string;
        public Value: string;
        public FailureReason: string;

        constructor(id) {
            this.ID = id;
        }
    }

    export class EntityKey {
        /// <summary>
        /// Represents an entity's key.
        /// </summary>

        public HasValidEmptyColumnValue: boolean;

        public getHashKey(): string {
            /// <summary>
            /// Gets a hash key for this messaging.EntityKey.
            /// </summary>
            /// <returns type="String">A hash key for this messaging.EntityKey.</returns>

            return JSON.stringify(this);
        }
    }

    export class RowChange {
        /// <summary>
        /// Represents a data change of a row.
        /// </summary>
        /// <field name="OriginalRowIndex" type="Number">
        /// The index of the row in the table before changes were made.
        /// </field>
        /// <field name="NewRowIndex" type="Number">
        /// The index of the row in the table after changes were made.
        /// </field>
        /// <field name="Key" type="messaging.EntityKey">
        /// The row's key value.
        /// </field>
        /// <field name="Data" type="Object">
        /// The data changes made to the row if the row is being updated, or the values entered for
        /// the new row if the row is being created.
        /// </field>
        /// <field name="ETag" type="String">
        /// The OData-provided unique identifier of the row's state before being changed.
        /// </field>
        /// <field name="ErrorMessage" type="String">
        /// A message describing the error encountered while publishing the change, if any.
        /// </field>
        /// <field name="DisplayValueData" type="Object">
        /// A property bag containing objects keyed by dimension relationship name with the attribute values
        /// for the corresponding display value. Only defined if the row has a display value column.
        /// </field>

        public OriginalRowIndex: number;
        public NewRowIndex: number;
        public Key: EntityKey;
        public Data: any;
        public ETag: string;
        public ErrorMessage: string;
        public DisplayValueData: any;
    }

    export class DataChanges {
        /// <summary>
        /// Represents the cumulative data changes for an entity.
        /// </summary>
        /// <field name="CreatedRows" type="Array" elementType="messaging.RowChange">
        /// The messaging.RowChange objects representing the rows created in this set of data changes.
        /// </field>
        /// <field name="UpdatedRows" type="Array" elementType="messaging.RowChange">
        /// The messaging.RowChange objects representing the rows changed in this set of data changes.
        /// </field>
        /// <field name="DeletedRows" type="Array" elementType="messaging.RowChange">
        /// The messaging.RowChange objects representing the rows deleted in this set of data changes.
        /// </field>
        /// <field name="MovedRows" type="Array" elementType="messaging.RowChange">
        /// The messaging.RowChange objects representing the rows moved in this set of data changes.
        /// </field>
        /// <field name="DisplayValueFields" type="Array" elementType="String">
        /// The names of the display value fields with changes.
        /// </field>
        /// <field name="ChangedReadOnlyFields" type="Array" elementType="String">The names of the changed read-only fields.</field>
        /// <field name="ContainsErrors" type="Boolean">Indicates whether or not errors were encountered when calculating the data changes.</field>
        /// <field name="ErrorMessage" type="String">The error message indicating the errors that were encountered.</field>

        public CreatedRows: RowChange[];
        public UpdatedRows: RowChange[];
        public DeletedRows: RowChange[];
        public MovedRows: RowChange[];
        public DisplayValueFields: string[];
        public ChangedReadOnlyFields: string[];
        public ContainsErrors: boolean = false;
        public ErrorMessage: string = "";

        constructor() {
            this.CreatedRows = [];
            this.UpdatedRows = [];
            this.DeletedRows = [];
            this.MovedRows = [];
            this.DisplayValueFields = [];
            this.ChangedReadOnlyFields = [];
        }
    }

    /// <field name="RequestResponseMap" type="Object">The map between a give message request type and the response message.</field>
    export let RequestResponseMap = {};

    // Configure the request response map to contain all responses keyed by their appropriate request message type; this should be kept up to date for all requests / responses
    messaging.RequestResponseMap[messaging.MessageTypes.checkAuthenticationRequest] = messaging.Responses.CheckAuthenticationResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.crossDomainCheckAuthenticationRequest] = messaging.Responses.CrossDomainCheckAuthenticationResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.getConfigRequest] = messaging.Responses.GetConfigResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.getEnhancedConfigRequest] = messaging.Responses.GetEnhancedConfigResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.getDataRequest] = messaging.Responses.GetDataResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.getMetadataRequest] = messaging.Responses.GetMetadataResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.getLabelsRequest] = messaging.Responses.GetLabelsResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.getCustomLookupRequest] = messaging.Responses.GetCustomLookupResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.publishDataRequest] = messaging.Responses.PublishDataResponse;
    messaging.RequestResponseMap[messaging.MessageTypes.getUpdatedDataRequest] = messaging.Responses.GetUpdatedDataResponse;

    /// <field type="RegExp">The regular expression used to retrieve the resource ID from the window location's query string.</field>
    const ResourceIdQueryStringExpression = /^[^\?]+\?ResourceID=([^&]+)/;

    export function indicateResourceIsReady(): void {
        /// <summary>
        /// Indicates to the app that the current resource is ready.
        /// </summary>

        let resourceIdMatches = ResourceIdQueryStringExpression.exec(window.location.href);
        if (resourceIdMatches && resourceIdMatches.length === 2) {
            // eslint-disable-next-line @microsoft/sdl/no-postmessage-star-origin -- covered by threat model, only used during applet/resource init to send initial "ready" message
            window.parent.postMessage(new messaging.Requests.ResourceReadyRequest(resourceIdMatches[1]), "*");
        }
    }

    export class CustomLookupFieldDetails {
        /// <summary>
        /// Represents information about a field for the purpose of providing custom lookup actions with row context.
        /// </summary>
        /// <field name="FieldName" type="String">The name of the field.</field>
        /// <field name="SerializedValue" type="String">The serialized value of the field.</field>

        constructor(public FieldName: string, public SerializedValue: string) { }
    }

    export interface DataActivity {

        /** The ID of the activity. */
        ID: string;

        /** The duration (in millisconds) of the data activity. */
        Duration?: number;

        /** The number of records involved in the activity. */
        RecordCount?: number;

        /** Keeps track if the activity timed out or not. */
        DidTimeout?: boolean;
    }

    /** Handles messages for a given page. */
    export class MessageHandler {

        /** The message delegate used for the unknown message type message. */
        private unknownMessageType: (messageType: string) => string;

        /** The message handlers for the known messages the message listener will handle. */
        public _messageHandlers = new util.Map();

        /**
         * @param unknownMessageType The message delegate used for the unknown message type message. If provided
         *                           unknown message types will be handled (generic response); otherwise, they are
         *                           ignored.
         */
        constructor(unknownMessageType?: (messageType: string) => string) {
            this.unknownMessageType = unknownMessageType;
            this.messageListener = this.messageListener.bind(this);
        }

        public messageListener(event: MessageEvent): void {
            /// <summary>
            /// Listens for messages sent to this page.
            /// </summary>
            /// <param name="event" type="Object">The message event received by this listener.</param>

            let messageHandler = this._messageHandlers.get(event.data.Type);

            // If there was a message handler, call the handler; otherwise respond with an error
            if (messageHandler) {
                messageHandler(event);
            } else if (this.unknownMessageType) {
                let response = new messaging.Responses.UnknownMessageTypeResponse(event.data.Type);
                response.FailureReason = this.unknownMessageType(event.data.Type);
                (event.source as Window).postMessage(response, event.origin);
            }
        }

        public addHandler(messageType: string, handler: (event: MessageEvent) => void): void {
            /// <summary>
            /// Adds a handler for the provided message type, replacing any existing message handler for that type.
            /// </summary>
            /// <param name="messageType" type="String">The message type to add a handler for.</param>
            /// <param name="handler" type="Function">The handler to be used for the provided message type.</param>

            this._messageHandlers.add(messageType, handler);
        }

        public removeHandler(messageType: string): void {
            /// <summary>
            /// Removes all handlers for the provided message type.
            /// </summary>
            /// <param name="messageType" type="String">The message type to remove the handler for.</param>

            this._messageHandlers.remove(messageType);
        }
    }

    export class ErrorHandler {
        /// <summary>
        /// Handles error messages for a given page.
        /// </summary>

        constructor(private event: MessageEvent) {
            this.event = event;
            this.handleError = this.handleError.bind(this);
            this.handleAjaxError = this.handleAjaxError.bind(this);
        }

        public handleError(error: any, responseTouchup: (response: messaging.BaseResponse<BaseRequest>) => void = util.noop): void {
            /// <summary>
            /// Error handler used by message handlers to post a response corresponding to the event's
            /// request with an error status and failure reason defined.
            /// </summary>
            /// <param name="error" type="Object">
            /// A textual representation of the error or an error object with a 'message' property.
            /// </param>
            /// <param name="responseTouchup" type="Function" optional="true">The callback executed before sending the response, allowing any last minute response modifications.</param>

            let ResponseType = messaging.RequestResponseMap[this.event.data.Type];
            let response;

            if (ResponseType === undefined) {
                // If the corresponding response type wasn't found, respond with an unknown message type
                // response (note: this case should never be encountered as long as the 'messaging.RequestResponseMap'
                // object is kept in sync with requests and responses)
                response = new messaging.BaseResponse(messaging.MessageTypes.unknownMessageTypeResponse);
            } else {
                // If the corresponding response type was found, respond with a new instance of that type
                response = new ResponseType();
            }

            // Set the Status and FailureReason and then post the response
            response.Status = messaging.ResponseStatus.failure;
            response.Request = this.event.data;

            if (error.message !== undefined) {
                response.FailureReason = error.message;
            } else {
                response.FailureReason = error;
            }

            responseTouchup(response);

            (this.event.source as Window).postMessage(response, this.event.origin);
        }

        public handleAjaxError(request: any, textStatus: string, error: any, responseTouchup?: (response: messaging.BaseResponse<BaseRequest>) => void): void {
            /// <summary>
            /// Error handler used by message handlers when making AJAX calls.  Responds with the response, corresponding to the request,
            /// with an error status and failure reason.
            /// </summary>
            /// <param name="request" type="Object">The message request.</param>
            /// <param name="textStatus" type="String">The status of the request, in text form.</param>
            /// <param name="error" type="String">The textual portion of the actual error that was thrown.</param>
            /// <param name="responseTouchup" type="Function" optional="true">The callback executed before sending the response, allowing any last minute response modifications.</param>

            this.handleError(error, responseTouchup);
        }
    }
}
